import { useEffect, useState } from 'react'
import { templatesApiService } from '../services/templatesApiService'
import { settingsApiService } from '../services/settingsApiService'
import { ApiError } from '../services/BaseApiService'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'

const box: React.CSSProperties = {
  background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.sm, padding: spacing.md,
}
const EXPLAIN = 'Renders are still audited (who, when, template version, output hash), but neither the request payload nor the generated document is stored. Download, share links, preview links, rerender, e-signature and async/batch renders are unavailable.'

/** Template-level no-store. A team-wide no-store policy overrides it (most restrictive wins). */
export function TemplateNoStoreSetting({ templateId }: { templateId: string }) {
  const [state, setState] = useState<{ templateNoStore: boolean; teamNoStore: boolean } | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    templatesApiService.getPersistence(templateId).then(setState).catch(() => setState(null))
  }, [templateId])
  if (!state) return null

  async function toggle(noStore: boolean) {
    setError('')
    try { setState(await templatesApiService.setPersistence(templateId, noStore)) }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Failed to save') }
  }

  return (
    <div style={box}>
      <label style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', fontSize: fontSize.sm, fontWeight: 600, color: colors.text }}>
        <input type="checkbox" checked={state.templateNoStore || state.teamNoStore} disabled={state.teamNoStore}
          onChange={e => toggle(e.target.checked)} />
        Don't store payloads or documents for this template
      </label>
      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs }}>
        {state.teamNoStore ? 'On for every template by your team\'s policy. ' : ''}{EXPLAIN} Individual requests can also opt out with <code>"noStore": true</code> or the <code>X-DocMorphia-Store: none</code> header.
      </div>
      {error && <div role="alert" style={{ color: colors.error, fontSize: fontSize.sm }}>{error}</div>}
    </div>
  )
}

/** Team-wide no-store default (team admins). Templates and requests cannot turn storage back on. */
export function TeamNoStoreSetting() {
  const [noStore, setNoStore] = useState<boolean | null>(null)
  const [error, setError] = useState('')
  useEffect(() => { settingsApiService.getTeamPersistence().then(r => setNoStore(r.noStore)).catch(() => setNoStore(null)) }, [])
  if (noStore === null) return null

  async function toggle(value: boolean) {
    setError('')
    try { setNoStore((await settingsApiService.setTeamPersistence(value)).noStore) }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Failed to save') }
  }

  return (
    <div style={{ ...box, marginBottom: spacing.xl }}>
      <label style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', fontSize: fontSize.sm, fontWeight: 600, color: colors.text }}>
        <input type="checkbox" checked={noStore} onChange={e => toggle(e.target.checked)} />
        Never store render payloads or documents (team-wide)
      </label>
      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs }}>{EXPLAIN} Applies to every template; templates and requests cannot turn storage back on.</div>
      {error && <div role="alert" style={{ color: colors.error, fontSize: fontSize.sm }}>{error}</div>}
    </div>
  )
}
